import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Building2, FileText, Users, AlertCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LabelList } from 'recharts';
import axios from 'axios';

import { API_BASE as API } from '../config';

const STATUS_COLORS = { new: '#3b82f6', in_process: '#f59e0b', closed: '#34d399' };
const DEFAULT_COLORS = ['#6366f1', '#3b82f6', '#f59e0b', '#10b981', '#f97316', '#8b5cf6', '#06b6d4', '#e11d55'];

export default function ProviderDashboard({ onSelectOrg }) {
  const { user, operatorContext } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isOperatorProviderCtx = user?.is_platform_operator && operatorContext?.accountType === 'provider' && operatorContext?.tenantId;
  const isRealProvider = user?.account_type === 'provider';

  useEffect(() => {
    if (!isRealProvider && !isOperatorProviderCtx) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/dashboard/provider-overview`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isRealProvider, isOperatorProviderCtx, user?.tenant_id, operatorContext?.tenantId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!data) return null;

  const visibleOrgs = data.org_stats.filter(o => o.data_visible && o.org_id);
  const claimsChartData = visibleOrgs.map(o => ({ name: o.name, claims: o.claims, color: o.branding_color }));
  const statusPieData = [
    { name: 'New', value: data.total_new },
    { name: 'In Process', value: data.total_in_process },
    { name: 'Closed', value: data.total_closed },
  ].filter(d => d.value > 0);
  const totalStatusPie = statusPieData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="min-h-screen bg-gray-200 p-4 md:p-8" data-testid="provider-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header — uses the tenant/provider name so the same screen
         *  shows "Acme Insurance Dashboard" rather than the generic
         *  "Provider Dashboard" label. Operator-impersonation context
         *  overrides with the tenant being impersonated. */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-testid="provider-dashboard-title">
            {(() => {
              const tenantName = isOperatorProviderCtx
                ? (operatorContext?.tenantName || 'Provider')
                : (user?.tenant_name || 'Provider');
              return `${tenantName} Dashboard`;
            })()}
          </h1>
          <p className="text-sm text-gray-500">Overview across all organizations. Select an org from the switcher for detailed views.</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-6">
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 mx-auto text-indigo-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{data.org_count}</p>
              <p className="text-xs text-gray-500">Organizations</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <FileText className="w-5 h-5 mx-auto text-blue-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{data.total_claims}</p>
              <p className="text-xs text-gray-500">Total Claims</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 mx-auto text-teal-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{data.total_companies}</p>
              <p className="text-xs text-gray-500">Companies</p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-5 h-5 mx-auto text-amber-500 mb-1" />
              <p className="text-2xl font-bold text-gray-900">{data.total_new + data.total_in_process}</p>
              <p className="text-xs text-gray-500">Open Claims</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          {/* Claims by Org bar chart */}
          <Card className="lg:col-span-2 border-none shadow-sm bg-white">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Claims by Organization</h3>
              {claimsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={claimsChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="claims" radius={[0, 4, 4, 0]}>
                      {claimsChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No claims data yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Status breakdown pie */}
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Claim Statuses</h3>
              {statusPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      strokeWidth={0}
                    >
                      {statusPieData.map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.name.toLowerCase().replace(' ', '_')] || '#94a3b8'} />
                      ))}
                      <LabelList dataKey="name" position="outside" offset={12} style={{ fontSize: 11 }} />
                    </Pie>
                    <Tooltip formatter={(v) => `${v} (${totalStatusPie > 0 ? ((v / totalStatusPie) * 100).toFixed(0) : 0}%)`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No status data yet.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Per-org cards */}
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Organization Breakdown</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {data.org_stats.map((org, i) => (
            <Card
              key={org.org_id || 'unassigned'}
              className={`border-none shadow-sm bg-white cursor-pointer hover:shadow-md transition-shadow ${!org.data_visible ? 'opacity-50' : ''}`}
              onClick={() => org.org_id && onSelectOrg?.(org.org_id)}
              data-testid={`org-stat-card-${org.org_id || 'unassigned'}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: org.branding_color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] }}
                  >
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">{org.name}</h4>
                    <p className="text-xs text-gray-400">{org.companies} companies</p>
                  </div>
                  {!org.data_visible && <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Hidden</span>}
                  {org.org_id && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                </div>

                {/* Status bar */}
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-gray-500">New {org.new}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-gray-500">In Process {org.in_process}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-gray-500">Closed {org.closed}</span>
                  </span>
                </div>

                {/* Total claims bar */}
                {org.claims > 0 && (
                  <div className="mt-2 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, (org.claims / Math.max(...data.org_stats.map(o => o.claims), 1)) * 100)}%`, backgroundColor: org.branding_color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] }} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
